import { forwardRef, useState, useEffect } from "react";
import type { ReactElement, ReactNode, Ref } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Slide,
  Switch,
  Typography,
} from "@mui/material";
import ControlledTextField from "components/inputs/ControlledTextField";
import type { EtablissementPublic, EtablissementPublicModelForm } from "models";
import { enqueueSnackbar } from "notistack";
import type { TransitionProps } from "notistack";
import { FormProvider, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import ToastMessages from "constants/ToastMessages";
import { useSnackbarErrorHandler } from "utils/errorHandling";
import { useNavigate } from "react-router";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { emailRules, phoneAcceptedFormatsHelperText, phoneRules } from "constants/InputRules";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement;
  },
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * On vérifie si au moins un champ est renseigné dans le formulaire
 */
function validate1ChampRenseigne(values: Array<string | undefined | null>): boolean | string {
  return (
    // `JS for noobies`:
    values
      // 1. L'utilisation de la méthode `.filter(Boolean)` permet d'éliminer
      //    toutes valeurs _falsy_, et donc les `undefined`, `null` et `""` (chaine vide).
      .filter(Boolean)
      // 2. Suivi par l'appel à `.join("")` permet de concaténer toutes les valeurs du tableaux
      //    (sans séparateur étant donné l'argument `""`).
      .join("")
      // 3. Finalement, l'utilisation de la méthode `.trim()` permet de s'assurer d'éliminer toutes
      //    valeurs résultantes de la concaténation de chaines d'espaces uniquement.
      .trim() !== "" ||
    // 4. Finalement, si la comparaison avec une chaine vide n'est pas vérifiée, retour d'un texte
    //    pouvant être utilisé au niveau de l'input pour indiquer la validation en erreur.
    "Au moins un changement est requis pour enregister (Email, Téléphone ou Adresse postale)."
  );
}

/**
 * On check si au moins un des trois champs de l'adresse est rempli
 */
function checkAdresse(values: Array<string | undefined | null>): boolean {
  return values.filter(Boolean).join("").trim() !== "" || false;
}

interface EtablissementPublicFormProps {
  etablissementPublic?: Partial<EtablissementPublic>;
  open: boolean;
  idCollectivite: string; // codeInsee ou SIREN d'un EPCI
  setOpen: (b: boolean) => void;
  upsertEtablissementCoordonnees: (id: string, e: EtablissementPublicModelForm) => Promise<void>;
  suppEtablissementCoordonnees: (id: string) => Promise<void>;
}

/**
 * Modal qui contient le formulaire de surcharge des coordonnées
 */
function EtablissementPublicForm({
  etablissementPublic,
  open,
  setOpen,
  idCollectivite,
  upsertEtablissementCoordonnees,
  suppEtablissementCoordonnees,
}: Readonly<EtablissementPublicFormProps>): ReactNode {
  const navigate = useNavigate();
  const [checked, setChecked] = useState<boolean>(etablissementPublic?.adresse != null);
  const formMethods = useForm<EtablissementPublicModelForm>({
    shouldFocusError: false,
  });
  const { handleSubmit, watch, reset } = formMethods;

  const { catchErrors, isLoading } = useSnackbarErrorHandler();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setChecked(event.target.checked);
  };

  const adresseValidationRule = {
    required: checked && "Pour la création d'une nouvelle adresse, ce champ est obligatoire.",
  };

  const oneValidationRule = {
    validate: () =>
      (checkAdresse([
        watch("adresse.codePostal"),
        watch("adresse.commune"),
        watch("adresse.numeroVoie"),
      ] as Array<string | undefined>) &&
        checked) ||
      validate1ChampRenseigne([watch("email"), watch("telephone")] as Array<string | undefined>),
  };

  function handleClose(): void {
    setOpen(false);
  }

  useEffect(() => {
    reset(etablissementPublic);
  }, [
    etablissementPublic,
    open,
    setOpen,
    idCollectivite,
    upsertEtablissementCoordonnees,
    suppEtablissementCoordonnees,
    reset,
  ]);

  const submitEtablissementPublic: SubmitHandler<EtablissementPublicModelForm> = async function (
    data: EtablissementPublicModelForm
  ): Promise<void> {
    async function createEtablissementPublic(): Promise<void> {
      const res: EtablissementPublicModelForm = {
        ...data,
        id: etablissementPublic?.id,
        nom: etablissementPublic?.nom,
        adresse: checked
          ? {
              numeroVoie: data.adresse?.numeroVoie?.trim(),
              codePostal: data.adresse?.codePostal?.trim(),
              commune: data.adresse?.commune?.trim(),
              latitude: undefined,
              longitude: undefined,
              serviceDistribution: data.adresse?.serviceDistribution?.trim(),
              complement1: data.adresse?.complement1?.trim(),
              complement2: data.adresse?.complement2?.trim(),
              typeAdresse: undefined,
            }
          : undefined,
        telephone: data.telephone === "" ? undefined : data.telephone?.trim(),
        email: data.email === "" ? undefined : data.email?.trim(),
      };
      await upsertEtablissementCoordonnees(idCollectivite, res);

      enqueueSnackbar({
        variant: "success",
        message: ToastMessages.CREATED_ETABLISSEMENT_PUBLIC,
      });
    }

    await catchErrors(createEtablissementPublic);

    handleClose();
    navigate(0);
  };

  const submitDeleteEtablissementPublic = async function (): Promise<void> {
    async function deleteEtablissementPublic(): Promise<void> {
      await suppEtablissementCoordonnees(idCollectivite);

      enqueueSnackbar({
        variant: "success",
        message: ToastMessages.DELETED_ETABLISSEMENT_PUBLIC,
      });
    }

    await catchErrors(deleteEtablissementPublic);

    handleClose();
    navigate(0);
  };

  return (
    <Dialog
      component="form"
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      slots={{ transition: Transition }}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(submitEtablissementPublic)}>
      <FormProvider {...formMethods}>
        <DialogTitle>Modification des coordonnées de {etablissementPublic?.nom}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid container size={12}>
              <Grid container size={12} sx={{ alignItems: "center" }}>
                <Grid size={6}>
                  <DialogContentText variant="h6" color="primary">
                    Informations de contact
                  </DialogContentText>
                </Grid>
                <Grid container size={6} sx={{ justifyContent: "flex-end" }}>
                  <Button
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={submitDeleteEtablissementPublic}
                    loading={isLoading}
                    variant="contained"
                    color="error"
                    disabled={etablissementPublic?.modifiedBy == null}
                    startIcon={<DeleteIcon />}
                    loadingPosition="start">
                    Supprimer les coordonnées
                  </Button>
                </Grid>
              </Grid>
              <Grid size={6}>
                <ControlledTextField
                  name="email"
                  defaultValue={etablissementPublic?.email}
                  fullWidth
                  label="Adresse email"
                  variant="outlined"
                  type="email"
                  placeholder="mairie@contact.fr"
                  rules={{
                    ...oneValidationRule,
                    ...emailRules,
                  }}
                />
              </Grid>
              <Grid size={6}>
                <ControlledTextField
                  name="telephone"
                  defaultValue={etablissementPublic?.telephone}
                  fullWidth
                  label="Téléphone"
                  variant="outlined"
                  placeholder="03.00.00.00.00"
                  rules={{
                    ...oneValidationRule,
                    ...phoneRules,
                  }}
                  defaultHelperText={phoneAcceptedFormatsHelperText}
                />
              </Grid>
            </Grid>
            <Grid container spacing={0} size={12} sx={{ alignItems: "center" }}>
              <Typography sx={{ mr: 2 }}>
                Voulez-vous changer l'adresse postale de la commune ?
              </Typography>
              <Grid container sx={{ alignItems: "center" }}>
                <Typography>Non</Typography>
                <Switch checked={checked} onChange={handleChange} />
                <Typography>Oui</Typography>
              </Grid>
            </Grid>
            {checked && (
              <Grid container spacing={2} size={12}>
                <Grid size={12}>
                  <DialogContentText variant="h6" color="primary">
                    Adresse
                  </DialogContentText>
                </Grid>
                <Grid size={6}>
                  <ControlledTextField
                    name="adresse.numeroVoie"
                    defaultValue={etablissementPublic?.adresse?.numeroVoie}
                    fullWidth
                    label="Rue et voie *"
                    variant="outlined"
                    placeholder="4 impasse des alouettes"
                    rules={adresseValidationRule}
                    disabled={!checked}
                  />
                </Grid>
                <Grid size={6}>
                  <ControlledTextField
                    name="adresse.serviceDistribution"
                    defaultValue={etablissementPublic?.adresse?.serviceDistribution}
                    fullWidth
                    label="Service de distribution"
                    variant="outlined"
                    placeholder="BP 7003"
                    disabled={!checked}
                  />
                </Grid>
                <Grid size={6}>
                  <ControlledTextField
                    name="adresse.complement1"
                    defaultValue={etablissementPublic?.adresse?.complement1}
                    fullWidth
                    label="Complement d'adresse 1"
                    variant="outlined"
                    placeholder="1er étage, batiment A ..."
                    disabled={!checked}
                  />
                </Grid>
                <Grid size={6}>
                  <ControlledTextField
                    name="adresse.complement2"
                    defaultValue={etablissementPublic?.adresse?.complement2}
                    fullWidth
                    label="Complement d'adresse 2"
                    variant="outlined"
                    placeholder="1er étage, batiment A ..."
                    disabled={!checked}
                  />
                </Grid>
                <Grid size={6}>
                  <ControlledTextField
                    name="adresse.codePostal"
                    defaultValue={etablissementPublic?.adresse?.codePostal}
                    fullWidth
                    label="Code postal *"
                    variant="outlined"
                    placeholder="67350"
                    rules={adresseValidationRule}
                    disabled={!checked}
                  />
                </Grid>
                <Grid size={6}>
                  <ControlledTextField
                    name="adresse.commune"
                    defaultValue={etablissementPublic?.adresse?.commune}
                    fullWidth
                    label="Commune *"
                    variant="outlined"
                    placeholder="Val-de-Moder"
                    rules={adresseValidationRule}
                    disabled={!checked}
                  />
                </Grid>
              </Grid>
            )}
            <Typography variant="caption">
              Attention ! Les données saisies ici ont priorité sur les données publiques dans les
              différents exports.
            </Typography>
            <Grid container size={12} sx={{ justifyContent: "flex-end" }}>
              <Grid size="auto">
                <Button variant="text" onClick={handleClose} color="error">
                  Annuler
                </Button>
              </Grid>
              <Grid size="auto">
                <Button
                  variant="contained"
                  loading={isLoading}
                  type="submit"
                  startIcon={<SaveIcon />}
                  loadingPosition="start">
                  Enregister les modifications
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
}

export default EtablissementPublicForm;
